import * as React from 'react'
import { tabularContractProblems, type TabularContractProblem } from '@atbash-labs/ontology-core'
import type { OutputTable } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '../../../components/ui/dialog'
import { ContractTablesEditor } from './ContractTablesEditor'
import {
  EDITOR_DIALOG_CLASS,
  EditorDialogHeader,
  EditorError,
  EditorField,
  EditorProblems
} from './editorChrome'

export { OUTPUT_COLUMN_TYPES } from './ContractTablesEditor'

export interface ContractEditorSubmit {
  tables: OutputTable[]
  note: string
}

/**
 * Tables, write mode and key, columns, types, required flags and hints.
 * Submitting starts an update build (`reason.kind = contract_edit`) over the
 * stored examples; the builder rewrites the scripts to the new shape and
 * proposes a draft. Checks are not here — they change without a rebuild.
 */
export function ContractEditorDialog({
  open,
  onOpenChange,
  initial,
  loading,
  error,
  mode = 'update',
  parserKind = 'script',
  finalizeEntrypoint,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial: OutputTable[]
  loading: boolean
  error?: string
  mode?: 'update' | 'draft'
  parserKind?: 'script' | 'agent'
  /** Set when a finalize script writes the output tables from page tables this dialog can't edit. */
  finalizeEntrypoint?: string
  onSubmit: (input: ContractEditorSubmit) => void
}) {
  const [tables, setTables] = React.useState<OutputTable[]>(initial)
  const [note, setNote] = React.useState('')
  // Snapshot the starting point when the dialog opens — not whenever the
  // parent re-renders with a fresh `initial` identity mid-edit.
  const initialRef = React.useRef(initial)
  initialRef.current = initial
  React.useEffect(() => {
    if (open) {
      setTables(initialRef.current)
      setNote('')
    }
  }, [open])

  const problems = validateContract(tables)
  const unchanged = sameContract(tables, initial)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${EDITOR_DIALOG_CLASS} max-h-[92vh] sm:max-w-5xl`}>
        <EditorDialogHeader title="Edit contract">
          {mode === 'update' && parserKind === 'agent'
            ? 'Pinned exactly as you leave it. The builder updates finalize and the page tables as far as the edit requires, and the example pages are read again with the result before it parks a draft for review. Nothing changes until you save that draft.'
            : 'Changing the tables or columns re-runs the builder over the stored example files and proposes a new draft for review. Nothing changes until you save that draft.'}
        </EditorDialogHeader>
        {finalizeEntrypoint ? (
          <p className="text-11 text-ink-3" data-testid="contract-finalize-note">
            On this config <span className="font-mono">{finalizeEntrypoint}</span> fills the output
            columns from what the vision call reads on each page, so verification fails if an edit
            no longer matches what it writes. Changing what is read from each page takes a new
            build.
          </p>
        ) : null}
        <ContractTablesEditor tables={tables} onChange={setTables} />
        {mode === 'update' ? (
          <EditorField
            label="Why this change (required)"
            ariaLabel="Reason"
            placeholder="e.g. split the location column"
            value={note}
            onChange={setNote}
          />
        ) : null}
        <EditorProblems problems={problems} testId="contract-problems" />
        <EditorError error={error} />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={
              loading || problems.length > 0 || unchanged || (mode === 'update' && !note.trim())
            }
            onClick={() => onSubmit({ tables: normaliseContract(tables), note: note.trim() })}
          >
            {mode === 'update' ? 'Start update build' : 'Apply edits'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function normaliseContract(tables: OutputTable[]): OutputTable[] {
  return tables.map((table) => ({
    table: table.table.trim(),
    mode: table.mode,
    key: table.key.map((k) => k.trim()).filter(Boolean),
    ...(table.ingestOncePerAsOfDate ? { ingestOncePerAsOfDate: true } : {}),
    columns: table.columns.map((column) => ({
      name: column.name.trim(),
      type: column.type,
      ...(column.required ? { required: true } : {}),
      ...(column.description?.trim() ? { description: column.description.trim() } : {})
    }))
  }))
}

export function sameContract(a: OutputTable[], b: OutputTable[]): boolean {
  return JSON.stringify(normaliseContract(a)) === JSON.stringify(normaliseContract(b))
}

/**
 * The shape rules the service enforces, applied here so the button only
 * enables a contract the service will take. The rules themselves live in
 * `tabularContractProblems`; this only puts them into sentences. Names are
 * compared folded, as DuckDB does: `Sku` and `sku` are one column, and a key
 * of `SKU` refers to it.
 */
export function validateContract(tables: OutputTable[]): string[] {
  return tabularContractProblems(normaliseContract(tables)).map(describeProblem)
}

const FOLDED = '(names differing only by case are the same)'

function describeProblem(problem: TabularContractProblem): string {
  switch (problem.kind) {
    case 'no_tables':
      return 'At least one table is required.'
    case 'table_name_invalid':
      return `Table name “${problem.table}” must be an identifier.`
    case 'table_name_duplicate':
      return `Table “${problem.table}” is listed twice ${FOLDED}.`
    case 'no_columns':
      return `Table “${problem.table}” needs at least one column.`
    case 'column_name_invalid':
      return `Column “${problem.column}” in ${problem.table} must be an identifier.`
    case 'column_name_duplicate':
      return `Column “${problem.column}” is listed twice in ${problem.table} ${FOLDED}.`
    case 'key_required':
      return `Table “${problem.table}” needs a key for ${
        problem.because === 'upsert' ? 'upsert' : 'once per as-of date'
      }.`
    case 'key_column_duplicate':
      return `Key column “${problem.column}” is listed twice for ${problem.table} ${FOLDED}.`
    case 'key_not_a_column':
      return `Key column “${problem.column}” is not a column of ${problem.table}.`
  }
}
